import { useState } from 'react';
import type { Guide } from '../../core/guides';
import { addGuide, rotateViewBy, setViewRotation } from '../../state/actions';
import { useAppStore } from '../../state/store';
import { Checkbox, Modal, NumberInput, Select, SliderRow } from '../components/controls';

const AXES: { value: Guide['axis']; label: string }[] = [
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'vertical', label: 'Vertical' },
];

/** View - Rotate View: turns the canvas on screen without touching pixels. */
export function RotateViewDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const docId = useAppStore((state) => state.activeDocumentId);
  const rotation = useAppStore((state) => (docId ? (state.views[docId]?.rotation ?? 0) : 0));

  return (
    <Modal
      open={open}
      title="Rotate View"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={() => setViewRotation(0)}>
            Reset
          </button>
          <button type="button" className="button button--primary" onClick={onClose}>
            Done
          </button>
        </>
      }
    >
      <p className="dialog__note">
        Turning the view only changes what you see; the document itself is unchanged.
      </p>
      <SliderRow label="Angle" value={rotation} min={0} max={359} onChange={setViewRotation} />
      <div className="dialog__row">
        <button type="button" className="button" onClick={() => rotateViewBy(-15)}>
          -15°
        </button>
        <button type="button" className="button" onClick={() => rotateViewBy(15)}>
          +15°
        </button>
        <button type="button" className="button" onClick={() => rotateViewBy(90)}>
          +90°
        </button>
      </div>
    </Modal>
  );
}

export function NewGuideDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [axis, setAxis] = useState<Guide['axis']>('vertical');
  const [position, setPosition] = useState(0);

  const apply = () => {
    addGuide(axis, position);
    onClose();
  };

  return (
    <Modal
      open={open}
      title="New Guide"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="button button--primary" onClick={apply}>
            Add
          </button>
        </>
      }
    >
      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Orientation
        </span>
        <Select value={axis} options={AXES} onChange={setAxis} />
      </div>
      <div className="field">
        <span className="field__label" style={{ width: 66 }}>
          Position
        </span>
        <NumberInput value={position} min={-10000} max={10000} onChange={setPosition} />
        <span className="field__suffix">px</span>
      </div>
    </Modal>
  );
}

export function GridSettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const grid = useAppStore((state) => state.grid);
  const patchGrid = useAppStore((state) => state.patchGrid);
  const snap = useAppStore((state) => state.snap);
  const patchSnap = useAppStore((state) => state.patchSnap);

  return (
    <Modal
      open={open}
      title="Grid and Snapping"
      onClose={onClose}
      footer={
        <button type="button" className="button button--primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div className="field">
        <span className="field__label" style={{ width: 92 }}>
          Gridline every
        </span>
        <NumberInput
          value={grid.size}
          min={2}
          max={4096}
          onChange={(size) => patchGrid({ size })}
        />
        <span className="field__suffix">px</span>
      </div>
      <div className="field">
        <span className="field__label" style={{ width: 92 }}>
          Subdivisions
        </span>
        <NumberInput
          value={grid.subdivisions}
          min={1}
          max={32}
          onChange={(subdivisions) => patchGrid({ subdivisions })}
        />
      </div>
      <Checkbox
        label="Snap"
        checked={snap.enabled}
        onChange={(enabled) => patchSnap({ enabled })}
      />
      <Checkbox
        label="Snap to guides"
        checked={snap.toGuides}
        onChange={(toGuides) => patchSnap({ toGuides })}
      />
      <Checkbox
        label="Snap to grid"
        checked={snap.toGrid}
        onChange={(toGrid) => patchSnap({ toGrid })}
      />
      <Checkbox
        label="Snap to document bounds"
        checked={snap.toDocument}
        onChange={(toDocument) => patchSnap({ toDocument })}
      />
    </Modal>
  );
}
